import { forwardRef, type ReactNode } from 'react';
import { motion, type HTMLMotionProps } from 'motion/react';
import { spring } from '../motion/presets';
import { cx } from '../internal/cx';
import type { ButtonVariant } from './Button';

export type IconButtonProps = Omit<HTMLMotionProps<'button'>, 'children'> & {
  /** Libellé accessible OBLIGATOIRE (aria-label). */
  label: string;
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg';
  children?: ReactNode;
};

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-panda-500 text-ink-950 hover:shadow-glow-panda hover:brightness-110',
  secondary: 'bg-ink-700 text-ink-100 border border-ink-600 hover:border-panda-500/60',
  ghost: 'bg-transparent text-ink-100 hover:bg-ink-100/10',
  danger: 'bg-danger-500 text-white hover:brightness-110',
};

const SIZES: Record<NonNullable<IconButtonProps['size']>, string> = {
  sm: 'h-9 w-9',
  md: 'h-11 w-11', // 44px
  lg: 'min-h-[3.25rem] min-w-[3.25rem]',
};

/** Bouton rond icône seule — `label` requis pour les lecteurs d'écran. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, variant = 'ghost', size = 'md', className, children, type = 'button', ...rest },
  ref,
) {
  return (
    <motion.button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      whileTap={rest.disabled ? undefined : { scale: 0.92 }}
      transition={spring.snappy}
      className={cx(
        'inline-flex select-none items-center justify-center rounded-full',
        'transition-[box-shadow,filter,background-color,border-color] duration-fast ease-fun',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {children}
    </motion.button>
  );
});
